import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const DAY_LABELS = { Mon:'lunes', Tue:'martes', Wed:'miércoles', Thu:'jueves', Fri:'viernes' }
const URGENCY_BG = { 1:'#fff5f5', 2:'#fffbf0', 3:'#f9f9f9' }
const URGENCY_BORDER = { 1:'#fecaca', 2:'#fde68a', 3:'#e3e5e8' }
const URGENCY_DOT = { 1:'#ef4444', 2:'#f59e0b', 3:'#d1d5db' }
const TAG_STYLE = {
  auto:   { background:'#e1f5ee', color:'#085041' },
  manual: { background:'#e6f1fb', color:'#0c447c' },
  urgent: { background:'#fcebeb', color:'#a32d2d' },
}
const TAG_LABEL = { auto:'Auto', manual:'Tarea', urgent:'Urgente' }
const ALERT_COLORS = {
  red:   { bg:'#fcebeb', dot:'#e24b4a', color:'#a32d2d' },
  amber: { bg:'#faeeda', dot:'#ef9f27', color:'#854f0b' },
  green: { bg:'#eaf3de', dot:'#639922', color:'#3b6d11' },
}

const ITEMS_PER_PAGE = 10

function getWeekBounds() {
  const now = new Date()
  const dow = now.getDay()
  const mon = new Date(now)
  mon.setDate(now.getDate() - (dow === 0 ? 6 : dow - 1))
  mon.setHours(0,0,0,0)
  const sun = new Date(mon)
  sun.setDate(mon.getDate() + 6)
  return { monStr: mon.toISOString().slice(0,10), sunStr: sun.toISOString().slice(0,10) }
}

const QUICK_LINKS = [
  { label:'Instagram', url:'https://www.instagram.com/kittys.cleaningservices/', color:'#e1306c' },
  { label:'Facebook',  url:'https://www.facebook.com/profile.php?id=61589303193123', color:'#1877f2' },
  { label:'Yelp',      url:'https://biz.yelp.com/home/4uaYr7-SCAckHTsE_G-zrA/', color:'#d32323' },
]

function NoteColumn({ noteKey, label, color, border, titleColor }) {
  const [entries, setEntries] = useState(() => {
    try { return JSON.parse(localStorage.getItem(noteKey)||'[]') } catch(e) { return [] }
  })
  const [draft, setDraft] = useState('')

  function addNote() {
    if (!draft.trim()) return
    const updated = [{ text:draft.trim(), date:new Date().toLocaleDateString('es-MX',{month:'short',day:'numeric'}) }, ...entries]
    setEntries(updated)
    localStorage.setItem(noteKey, JSON.stringify(updated))
    setDraft('')
  }
  function deleteNote(i) {
    const updated = entries.filter((_,idx)=>idx!==i)
    setEntries(updated)
    localStorage.setItem(noteKey, JSON.stringify(updated))
  }

  return (
    <div style={{background:color,borderRadius:12,border:`1px solid ${border}`,padding:'12px',display:'flex',flexDirection:'column',minHeight:180}}>
      <div style={{fontSize:12,fontWeight:600,color:titleColor,marginBottom:8}}>{label}</div>
      <div style={{display:'flex',gap:4,marginBottom:8}}>
        <input style={{flex:1,fontSize:11,padding:'5px 8px',border:`1px solid ${border}`,borderRadius:7,background:'#fff',color:'#202124'}}
          value={draft} placeholder="Nueva nota..."
          onChange={e=>setDraft(e.target.value)}
          onKeyDown={e=>e.key==='Enter'&&addNote()}/>
        <button style={{fontSize:12,padding:'4px 8px',border:'none',borderRadius:7,background:titleColor,color:'#fff',cursor:'pointer'}}
          onClick={addNote}>+</button>
      </div>
      <div style={{flex:1,overflowY:'auto',display:'flex',flexDirection:'column',gap:4}}>
        {entries.map((e,i)=>(
          <div key={i} style={{background:'rgba(255,255,255,0.7)',borderRadius:7,padding:'6px 8px',fontSize:11,color:'#333',display:'flex',alignItems:'flex-start',gap:5}}>
            <div style={{flex:1,lineHeight:1.5}}>{e.text}</div>
            <div style={{fontSize:9,color:'#9aa0a6',whiteSpace:'nowrap',paddingTop:2}}>{e.date}</div>
            <button style={{fontSize:12,border:'none',background:'none',color:'#bdc1c6',cursor:'pointer',padding:0,lineHeight:1}} onClick={()=>deleteNote(i)}>×</button>
          </div>
        ))}
      </div>
    </div>
  )
}

// Supabase caps each request at 1000 rows: page through to get them all
async function pageAll(makeQuery) {
  const all = []
  for (let from = 0; ; from += 1000) {
    const { data, error } = await makeQuery().range(from, from + 999)
    if (error) return { data: all, error }
    all.push(...(data || []))
    if (!data || data.length < 1000) break
  }
  return { data: all }
}

// Local YYYY-MM-DD (toISOString would switch to UTC and roll the date after 5 PM)
function localDateStr(d) {
  return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0')
}

// How many days between visits each frequency expects
const FREQ_DAYS = { weekly:7, biweekly:14, triweekly:21, fortweekly:28, monthly:31, bimonthly:62 }
const FREQ_SHORT = { weekly:'semanal', biweekly:'c/2 sem', triweekly:'c/3 sem', fortweekly:'c/4 sem', monthly:'mensual', bimonthly:'c/2 meses' }
const GRACE_DAYS = 7          // one extra week before calling it late
const LOOKBACK_DAYS = 150     // how far back to look for a visit

function OverdueClientsPanel({ slim }) {
  const [overdue, setOverdue] = useState([])
  const [longGone, setLongGone] = useState([])
  const [showLongGone, setShowLongGone] = useState(false)

  useEffect(() => {
    async function load() {
      const today = new Date()
      const todayStr = localDateStr(today)
      const from = new Date(today); from.setDate(today.getDate() - LOOKBACK_DAYS)
      const fromStr = localDateStr(from)

      const [{ data: clients }, { data: history }, { data: jobs }] = await Promise.all([
        supabase.from('clients').select('id,name,nickname,frequency').eq('is_active', true),
        pageAll(() => supabase.from('cleaning_history').select('id,client_id,cleaned_date').gte('cleaned_date', fromStr).order('cleaned_date').order('id')),
        // Any job that wasn't skipped counts: past ones are visits, future ones mean they're already booked
        pageAll(() => supabase.from('jobs').select('id,client_id,scheduled_date,status').gte('scheduled_date', fromStr).in('status', ['scheduled', 'completed']).order('scheduled_date').order('id')),
      ])

      const lastVisit = {}
      const booked = new Set()
      const seen = (id, d) => { if (d && (!lastVisit[id] || d > lastVisit[id])) lastVisit[id] = d }
      ;(history || []).forEach(h => seen(h.client_id, h.cleaned_date))
      ;(jobs || []).forEach(j => {
        if (j.scheduled_date <= todayStr) seen(j.client_id, j.scheduled_date)
        else booked.add(j.client_id)
      })

      const late = [], gone = []
      ;(clients || []).forEach(c => {
        const every = FREQ_DAYS[c.frequency]
        if (!every) return                 // one-time, custom, etc.
        if (booked.has(c.id)) return       // already has an upcoming cleaning
        const last = lastVisit[c.id]
        if (!last) { gone.push(c); return }
        const days = Math.floor((new Date(todayStr + 'T12:00:00') - new Date(last + 'T12:00:00')) / 86400000)
        if (days > every + GRACE_DAYS) late.push({ ...c, lastDate: last, weeks: Math.floor(days / 7), lateBy: days - every })
      })
      late.sort((a, b) => b.lateBy - a.lateBy)
      gone.sort((a, b) => (a.nickname || a.name).localeCompare(b.nickname || b.name))
      setOverdue(late)
      setLongGone(gone)
    }
    load()
  }, [])

  const fmt = d => new Date(d + 'T12:00:00').toLocaleDateString('es-MX', { month: 'short', day: 'numeric' })
  const chip = { fontSize: 11, padding: '3px 9px', background: '#fff8e1', borderRadius: 20, border: '1px solid #fde68a', color: '#854f0b', whiteSpace: 'nowrap' }

  return (
    <div>
      <div style={{ fontSize: 11, color: '#80868b', marginBottom: 8 }}>
        Clientes que ya pasaron su frecuencia por más de una semana y no tienen limpieza programada.
      </div>
      {overdue.length === 0
        ? <div style={{ fontSize: 12, color: '#1d9e75' }}>Todos al día ✓</div>
        : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {overdue.map(c => (
              <div key={c.id} style={chip} title={`Última visita: ${fmt(c.lastDate)} · ${FREQ_SHORT[c.frequency] || ''}`}>
                {c.nickname || c.name} · <strong>{c.weeks} sem</strong> <span style={{ color: '#b7791f' }}>({FREQ_SHORT[c.frequency]})</span>
              </div>
            ))}
          </div>}
      {longGone.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <button onClick={() => setShowLongGone(v => !v)}
            style={{ fontSize: 11, color: '#5f6368', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
            {showLongGone ? '▾' : '▸'} Sin visita en 5+ meses ({longGone.length}) · ¿siguen activos?
          </button>
          {showLongGone && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>
              {longGone.map(c => (
                <div key={c.id} style={{ ...chip, background: '#f1f3f4', border: '1px solid #e3e5e8', color: '#5f6368' }}>{c.nickname || c.name}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default function Dashboard() {
  const [todos, setTodos] = useState([])
  const [posts, setPosts] = useState([])
  const [stats, setStats] = useState({ jobs:0, clients:0, openTasks:0, revenue:0 })
  const [alerts, setAlerts] = useState([])
  const [alertIdx, setAlertIdx] = useState(0)
  const [dismissedAlerts, setDismissedAlerts] = useState([])
  const [newTodo, setNewTodo] = useState('')
  const [newPost, setNewPost] = useState('')
  const [loading, setLoading] = useState(true)
  const [todoPage, setTodoPage] = useState(0)
  const [postPage, setPostPage] = useState(0)
  const [todoView, setTodoView] = useState('all')
  const [newAssignee, setNewAssignee] = useState('all')
  const [selectedTodo, setSelectedTodo] = useState(null)
  const [doneFilter, setDoneFilter] = useState('active')

  useEffect(() => { fetchAll() }, [])

  // (Auto tasks for "ruta no completada" and "4 semanas sin limpieza" were removed:
  //  they relied on jobs being marked completed, which no longer happens, and filled Tareas with false alarms.
  //  The Sin visita reciente panel below replaces them.)

  async function fetchAll() {
    setLoading(true)
    const { monStr, sunStr } = getWeekBounds()
    const [{ data: td }, { data: bp }, { data: cl }, { data: jb }, { data: rev }, { data: weekly }] = await Promise.all([
      supabase.from('todos').select('*').order('urgency').order('created_at', { ascending: false }),
      supabase.from('bulletin_posts').select('*').order('pinned', { ascending: false }).order('created_at', { ascending: false }),
      supabase.from('clients').select('id', { count: 'exact' }).eq('is_active', true),
      supabase.from('jobs').select('id,client_id').in('status',['scheduled','completed']).gte('scheduled_date', monStr).lte('scheduled_date', sunStr),
      supabase.from('financials').select('amount').eq('type','income').gte('entry_date', monStr).lte('entry_date', sunStr),
      supabase.from('clients').select('id,name,frequency,preferred_day').eq('is_active',true).eq('frequency','weekly'),
    ])
    const weekRevenue = (rev||[]).reduce((sum,r) => sum + parseFloat(r.amount||0), 0)
    const scheduledIds = new Set((jb||[]).map(j => j.client_id))
    const unscheduled = (weekly||[]).filter(c => !scheduledIds.has(c.id))
    const dayAlerts = unscheduled.slice(0,3).map(c => ({
      type:'red',
      text:`${c.name} no está programada esta semana`,
      sub: c.preferred_day ? `Prefiere los ${DAY_LABELS[c.preferred_day]||c.preferred_day}` : 'Sin día preferido',
    }))
    setTodos(td||[])
    setPosts(bp||[])
    setAlerts(dayAlerts)
    setStats({ jobs:(jb||[]).length, clients:(cl||[]).length, openTasks:(td||[]).filter(t=>!t.done).length, revenue:weekRevenue })
    setLoading(false)
  }

  async function toggleTodo(id, done) {
    await supabase.from('todos').update({ done:!done, done_at:!done?new Date().toISOString():null }).eq('id',id)
    setTodos(prev => prev.map(t => t.id===id ? {...t,done:!done} : t))
    setStats(prev => ({...prev, openTasks:prev.openTasks+(done?1:-1)}))
  }

  async function deleteTodo(id) {
    await supabase.from('todos').delete().eq('id', id)
    setTodos(prev => prev.filter(t => t.id !== id))
    setStats(prev => ({...prev, openTasks: prev.openTasks - (todos.find(t=>t.id===id)?.done ? 0 : 1)}))
  }

  async function addTodo() {
    if (!newTodo.trim()) return
    const { data } = await supabase.from('todos').insert([{ text:newTodo.trim(), done:false, urgency:3, tag:'manual', meta:'Agregado manualmente', assigned_to: newAssignee }]).select().single()
    if (data) { setTodos(prev=>[data,...prev]); setStats(prev=>({...prev,openTasks:prev.openTasks+1})) }
    setNewTodo('')
    setTodoPage(0)
  }

  async function clearDone() {
    await supabase.from('todos').delete().eq('done',true)
    setTodos(prev=>prev.filter(t=>!t.done))
  }

  async function addPost() {
    if (!newPost.trim()) return
    const { data } = await supabase.from('bulletin_posts').insert([{ author:'Tú', body:newPost.trim(), pinned:false }]).select().single()
    if (data) setPosts(prev=>[data,...prev])
    setNewPost('')
    setPostPage(0)
  }

  async function deletePost(id) {
    await supabase.from('bulletin_posts').delete().eq('id', id)
    setPosts(prev => prev.filter(p => p.id !== id))
  }

  async function togglePin(id, pinned) {
    await supabase.from('bulletin_posts').update({ pinned: !pinned }).eq('id', id)
    const updated = posts.map(p => p.id===id ? {...p,pinned:!pinned} : p)
    setPosts([...updated.filter(p=>p.pinned), ...updated.filter(p=>!p.pinned)])
  }

  const now = new Date()
  const dayNames=['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado']
  const monthNames=['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre']
  const dateStr=`${dayNames[now.getDay()]}, ${now.getDate()} de ${monthNames[now.getMonth()]}`
  const hr=now.getHours()
  const greeting=hr<12?'¡Buenos días! ☀️':hr<18?'¡Buenas tardes!':'¡Buenas noches!'

  // Sorted todos: urgent first, then by urgency, done last
  // Week bounds for "done this week" 
  const openTasksCount = todos.filter(t=>!t.done).length

  const filteredTodos = (() => {
    let base = todoView==='all' ? todos : todos.filter(t=>t.assigned_to===todoView||t.assigned_to==='all')
    if (doneFilter==='active') return base.filter(t=>!t.done) // all incomplete tasks
    if (doneFilter==='week') return base.filter(t=>t.done && t.done_at && new Date(t.done_at)>=weekStart) // completed this week
    if (doneFilter==='archived') return base.filter(t=>t.done && (!t.done_at || new Date(t.done_at)<weekStart)) // completed before this week
    return base
  })()
  const sorted = doneFilter==='active'
    ? filteredTodos.sort((a,b)=>a.urgency-b.urgency) // pending: by urgency
    : filteredTodos.sort((a,b)=>new Date(b.done_at||0)-new Date(a.done_at||0)) // done: most recent first
  const todoPages = Math.ceil(sorted.length / ITEMS_PER_PAGE)
  const visibleTodos = sorted.slice(todoPage*ITEMS_PER_PAGE, (todoPage+1)*ITEMS_PER_PAGE)

  const postPages = Math.ceil(posts.length / ITEMS_PER_PAGE)
  const visiblePosts = posts.slice(postPage*ITEMS_PER_PAGE, (postPage+1)*ITEMS_PER_PAGE)

  if (loading) return <div style={{padding:32,color:'#80868b',textAlign:'center',fontSize:14}}>Cargando... 🌸</div>

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.greeting}>{greeting}</div>
        <div style={s.dateStr}>{dateStr}</div>
      </div>

      {/* Stats */}
      <div style={s.statRow}>
        {[
          { label:'Limpiezas esta semana', val:stats.jobs, fmt:'num' },
          { label:'Ingresos esta semana',  val:stats.revenue, fmt:'money', color:'#1d9e75' },
          { label:'Clientes activos',      val:stats.clients, fmt:'num' },
          { label:'Tareas pendientes', val:openTasksCount, fmt:'num', color:openTasksCount>0?'#e8547a':'#202124' },
        ].map((st,i)=>(
          <div key={i} style={s.stat}>
            <div style={{...s.statNum, color:st.color||'#202124'}}>
              {st.fmt==='money'?`$${st.val.toLocaleString('en-US',{minimumFractionDigits:0})}`:st.val}
            </div>
            <div style={s.statLbl}>{st.label}</div>
          </div>
        ))}
      </div>

      {/* Alerts — carousel */}
      {alerts.length>0 && (()=>{
        const visible = alerts.filter((_,i)=>!dismissedAlerts.includes(i))
        const idx = Math.min(alertIdx, Math.max(0, visible.length-1))
        if (visible.length===0) return null
        const a = visible[idx]
        const col = ALERT_COLORS[a?.type]||ALERT_COLORS.red
        return (
          <div style={{marginBottom:14}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
              <div style={s.secLabel}>Alertas</div>
              <div style={{fontSize:11,color:'#9aa0a6'}}>{idx+1} / {visible.length}</div>
              <div style={{marginLeft:'auto',display:'flex',gap:4}}>
                <button style={{fontSize:16,border:'none',background:'none',cursor:'pointer',color:'#9aa0a6',padding:'0 4px'}} onClick={()=>setAlertIdx(i=>Math.max(0,i-1))}>‹</button>
                <button style={{fontSize:16,border:'none',background:'none',cursor:'pointer',color:'#9aa0a6',padding:'0 4px'}} onClick={()=>setAlertIdx(i=>Math.min(visible.length-1,i+1))}>›</button>
              </div>
            </div>
            <div style={{...s.alert,background:col.bg,display:'flex',alignItems:'flex-start',gap:8}}>
              <div style={{...s.alertDot,background:col.dot,marginTop:4,flexShrink:0}}/>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:500,color:col.color}}>{a.text}</div>
                {a.sub&&<div style={{fontSize:11,color:col.color,opacity:.8,marginTop:2}}>{a.sub}</div>}
              </div>
              <button title="Ignorar alerta"
                style={{fontSize:18,border:'none',background:'none',cursor:'pointer',color:col.color,opacity:.4,padding:0,lineHeight:1,flexShrink:0}}
                onClick={()=>{
                  const origIdx = alerts.findIndex((orig,oi)=>!dismissedAlerts.includes(oi)&&orig===a)
                  setDismissedAlerts(d=>[...d,origIdx])
                  setAlertIdx(i=>Math.max(0,i-1))
                }}>×</button>
            </div>
          </div>
        )
      })()}

      {/* Quick links */}
      <div style={{marginBottom:14}}>
        <div style={s.secLabel}>Accesos rápidos</div>
        <div style={{display:'flex',gap:7,flexWrap:'wrap'}}>
          {QUICK_LINKS.map((l,i)=>(
            <a key={i} href={l.url} target="_blank" rel="noreferrer" style={{...s.quickLink, borderColor: l.color+'44'}}>
              <span style={{width:8,height:8,borderRadius:'50%',background:l.color,display:'inline-block',flexShrink:0}}/>
              <span style={{fontSize:12,fontWeight:500,color:l.color}}>{l.label}</span>
            </a>
          ))}
        </div>
      </div>

      <div style={s.cols}>
        {/* To-do */}
        <div style={s.panel}>
          <div style={s.panelHead}>
            <span style={s.panelTitle}>✅ Tareas</span>
            <div style={{display:'flex',gap:3,alignItems:'center'}}>
              {['all','yosi','mami'].map(v=>(
                <button key={v} style={{fontSize:10,padding:'2px 7px',borderRadius:6,border:'1px solid #e3e5e8',cursor:'pointer',
                  background:todoView===v?'#e8547a':'#fff',color:todoView===v?'#fff':'#80868b',fontWeight:todoView===v?500:400}}
                  onClick={()=>{setTodoView(v);setTodoPage(0)}}>
                  {v==='all'?'Todas':v==='yosi'?'Yosi':'Mami'}
                </button>
              ))}
              <button style={s.smBtn} onClick={clearDone}>Limpiar</button>
            </div>
          </div>
          <div style={{display:'flex',gap:0,padding:'4px 10px',borderBottom:'1px solid #e3e5e8',background:'#fafafa'}}>
            {[['active','Pendientes'],['week','Esta semana ✓'],['archived','Archivadas']].map(([v,l])=>(
              <button key={v} style={{fontSize:10,padding:'3px 9px',border:'none',background:'none',cursor:'pointer',
                color:doneFilter===v?'#e8547a':'#9aa0a6',fontWeight:doneFilter===v?600:400,
                borderBottom:doneFilter===v?'2px solid #e8547a':'2px solid transparent'}}
                onClick={()=>{setDoneFilter(v);setTodoPage(0)}}>
                {l}
              </button>
            ))}
          </div>
          <div style={s.scroll}>
            {visibleTodos.length===0&&<div style={s.empty}>Sin tareas pendientes 🎉</div>}
            {visibleTodos.map(t=>(
              <div key={t.id} style={{...s.todoRow,
                background: URGENCY_BG[t.urgency||3],
                borderLeft: `3px solid ${URGENCY_DOT[t.urgency||3]}`,
                borderBottom: `1px solid ${URGENCY_BORDER[t.urgency||3]}`,
                cursor:'pointer',paddingLeft:8,borderRadius:6,marginBottom:2}}
                onClick={()=>setSelectedTodo({...t})}>
                <div style={{...s.cb,...(t.done?s.cbDone:{})}}
                  onClick={e=>{e.stopPropagation();toggleTodo(t.id,t.done)}}>
                  {t.done?'✓':''}
                </div>
                <div style={{flex:1}}>
                  <div style={{...s.todoText,...(t.done?s.todoDone:{})}}>
                    {t.text}
                    <span style={{...s.tag,...(TAG_STYLE[t.tag]||TAG_STYLE.manual)}}>{TAG_LABEL[t.tag]||'Tarea'}</span>
                    {t.assigned_to && t.assigned_to !== 'all' && (
                      <span style={{...s.tag,background:'#fdeef3',color:'#c03060'}}>{t.assigned_to==='yosi'?'Yosi':'Mami'}</span>
                    )}
                  </div>
                  <div style={{display:'flex',gap:8,alignItems:'center',marginTop:1}}>
                    {t.meta&&<div style={s.todoMeta}>{t.meta}</div>}
                    {t.due_date&&<div style={{fontSize:10,color:new Date(t.due_date)<new Date()?'#ef4444':'#80868b'}}>📅 {new Date(t.due_date+'T12:00:00').toLocaleDateString('es-MX',{month:'short',day:'numeric'})}</div>}
                  </div>
                </div>
                <button style={s.deleteBtn} onClick={e=>{e.stopPropagation();deleteTodo(t.id)}}>×</button>
              </div>
            ))}
          </div>
          {/* Pagination */}
          {todoPages > 1 && (
            <div style={s.pagination}>
              <button style={s.pageBtn} disabled={todoPage===0} onClick={()=>setTodoPage(p=>p-1)}>←</button>
              {Array.from({length:todoPages},(_,i)=>(
                <button key={i} style={{...s.pageDot,...(todoPage===i?s.pageDotActive:{})}} onClick={()=>setTodoPage(i)}>{i+1}</button>
              ))}
              <button style={s.pageBtn} disabled={todoPage===todoPages-1} onClick={()=>setTodoPage(p=>p+1)}>→</button>
            </div>
          )}
          <div style={s.addRow}>
            <select style={{fontSize:11,padding:'6px 8px',border:'1px solid #e3e5e8',borderRadius:8,background:'#f8f9fa',color:'#5f6368'}}
              value={newAssignee} onChange={e=>setNewAssignee(e.target.value)}>
              <option value="all">Todas</option>
              <option value="yosi">Yosi</option>
              <option value="mami">Mami</option>
            </select>
            <input style={s.addInput} value={newTodo} placeholder="Nueva tarea..."
              onChange={e=>setNewTodo(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addTodo()}/>
            <button style={s.addBtn} onClick={addTodo}>Agregar</button>
          </div>
        </div>

        {/* Slim overdue clients */}
        <div style={{...s.panel,padding:'10px 14px'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
            <span style={s.panelTitle}>Sin visita reciente</span>
          </div>
          <OverdueClientsPanel slim={true}/>
        </div>
      </div>

      {/* Todo detail modal */}
      {selectedTodo && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.3)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:100}}
          onClick={()=>setSelectedTodo(null)}>
          <div style={{background:'#fff',borderRadius:14,padding:'20px 22px',width:360,boxShadow:'0 8px 32px rgba(0,0,0,0.15)'}}
            onClick={e=>e.stopPropagation()}>
            <div style={{fontSize:15,fontWeight:600,color:'#202124',marginBottom:12}}>Detalle de tarea</div>
            <div style={{marginBottom:10}}>
              <div style={{fontSize:10,color:'#80868b',marginBottom:3}}>Descripción</div>
              <input style={{width:'100%',padding:'7px 9px',border:'1px solid #e3e5e8',borderRadius:8,fontSize:13,background:'#f8f9fa'}}
                value={selectedTodo.text}
                onChange={e=>setSelectedTodo(t=>({...t,text:e.target.value}))}/>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:10}}>
              <div>
                <div style={{fontSize:10,color:'#80868b',marginBottom:3}}>Para</div>
                <select style={{width:'100%',padding:'6px 8px',border:'1px solid #e3e5e8',borderRadius:8,fontSize:12,background:'#f8f9fa'}}
                  value={selectedTodo.assigned_to||'all'}
                  onChange={e=>setSelectedTodo(t=>({...t,assigned_to:e.target.value}))}>
                  <option value="all">Todas</option>
                  <option value="yosi">Yosi</option>
                  <option value="mami">Mami</option>
                </select>
              </div>
              <div>
                <div style={{fontSize:10,color:'#80868b',marginBottom:3}}>Urgencia</div>
                <select style={{width:'100%',padding:'6px 8px',border:'1px solid #e3e5e8',borderRadius:8,fontSize:12,background:'#f8f9fa'}}
                  value={selectedTodo.urgency||3}
                  onChange={e=>setSelectedTodo(t=>({...t,urgency:parseInt(e.target.value)}))}>
                  <option value={1}>🔴 Alta</option>
                  <option value={2}>🟡 Media</option>
                  <option value={3}>⚪ Normal</option>
                </select>
              </div>
            </div>
            <div style={{marginBottom:10}}>
              <div style={{fontSize:10,color:'#80868b',marginBottom:3}}>Fecha límite</div>
              <input type="date" style={{width:'100%',padding:'6px 8px',border:'1px solid #e3e5e8',borderRadius:8,fontSize:12,background:'#f8f9fa'}}
                value={selectedTodo.due_date||''}
                onChange={e=>setSelectedTodo(t=>({...t,due_date:e.target.value}))}/>
            </div>
            <div style={{marginBottom:10}}>
              <div style={{fontSize:10,color:'#80868b',marginBottom:3}}>Notas adicionales</div>
              <textarea style={{width:'100%',padding:'7px 9px',border:'1px solid #e3e5e8',borderRadius:8,fontSize:12,background:'#f8f9fa',resize:'vertical',height:64}}
                value={selectedTodo.meta||''}
                placeholder="Detalles, contexto..."
                onChange={e=>setSelectedTodo(t=>({...t,meta:e.target.value}))}/>
            </div>
            <div style={{display:'flex',gap:6}}>
              <button style={{flex:1,padding:'7px',border:'1px solid #e3e5e8',borderRadius:8,background:'#fff',color:'#80868b',cursor:'pointer',fontSize:12}}
                onClick={()=>setSelectedTodo(null)}>Cancelar</button>
              <button style={{flex:2,padding:'7px',border:'none',borderRadius:8,background:'#e8547a',color:'#fff',fontWeight:500,cursor:'pointer',fontSize:12}}
                onClick={async()=>{
                  await supabase.from('todos').update({
                    text:selectedTodo.text,
                    assigned_to:selectedTodo.assigned_to,
                    urgency:selectedTodo.urgency,
                    meta:selectedTodo.meta,
                    due_date:selectedTodo.due_date||null
                  }).eq('id',selectedTodo.id)
                  setTodos(prev=>prev.map(t=>t.id===selectedTodo.id?{...t,...selectedTodo}:t))
                  setSelectedTodo(null)
                }}>Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const s = {
  page:{ padding:'16px 20px', overflowY:'auto', height:'100%' },
  header:{ marginBottom:16 },
  greeting:{ fontSize:20, fontWeight:600, color:'#202124' },
  dateStr:{ fontSize:12, color:'#80868b', marginTop:2 },
  statRow:{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:14 },
  stat:{ background:'#fff', borderRadius:12, padding:'14px 16px', border:'1px solid #e3e5e8' },
  statNum:{ fontSize:24, fontWeight:600, lineHeight:1 },
  statLbl:{ fontSize:11, color:'#80868b', marginTop:4 },
  secLabel:{ fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'.06em', color:'#9aa0a6', marginBottom:7 },
  alert:{ display:'flex', alignItems:'flex-start', gap:10, padding:'10px 12px', borderRadius:10, marginBottom:6 },
  alertDot:{ width:7, height:7, borderRadius:'50%', flexShrink:0, marginTop:4 },
  quickLink:{ display:'flex', alignItems:'center', gap:6, padding:'6px 12px', background:'#fff', border:'1px solid #e3e5e8', borderRadius:20, textDecoration:'none', color:'#202124' },
  cols:{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 },
  panel:{ background:'#fff', borderRadius:14, border:'1px solid #e3e5e8', display:'flex', flexDirection:'column', height:380 },
  panelHead:{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 14px', borderBottom:'1px solid #e3e5e8', flexShrink:0 },
  panelTitle:{ fontSize:13, fontWeight:600, color:'#202124' },
  smBtn:{ fontSize:11, padding:'3px 9px', border:'1px solid #e3e5e8', borderRadius:7, background:'none', color:'#80868b', cursor:'pointer' },
  scroll:{ flex:1, overflowY:'auto', padding:'8px 10px' },
  empty:{ fontSize:12, color:'#9aa0a6', textAlign:'center', padding:16 },
  todoRow:{ display:'flex', alignItems:'flex-start', gap:8, padding:'7px 0', borderBottom:'1px solid #f8f9fa' },
  cb:{ width:18, height:18, borderRadius:4, border:'1.5px solid #dadce0', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, flexShrink:0, marginTop:1 },
  cbDone:{ background:'#eaf3de', borderColor:'#97c459', color:'#3b6d11' },
  todoText:{ fontSize:12, color:'#202124', lineHeight:1.4 },
  todoDone:{ textDecoration:'line-through', color:'#9aa0a6' },
  tag:{ fontSize:9, padding:'1px 5px', borderRadius:3, marginLeft:5, fontWeight:500, verticalAlign:'middle' },
  todoMeta:{ fontSize:10, color:'#9aa0a6', marginTop:1 },
  deleteBtn:{ fontSize:16, border:'none', background:'none', color:'#ddd', cursor:'pointer', padding:'0 4px', flexShrink:0, lineHeight:1 },
  pagination:{ display:'flex', alignItems:'center', justifyContent:'center', gap:4, padding:'6px', borderTop:'1px solid #e3e5e8', flexShrink:0 },
  pageBtn:{ fontSize:12, padding:'2px 8px', border:'1px solid #e3e5e8', borderRadius:6, background:'none', cursor:'pointer', color:'#80868b' },
  pageDot:{ fontSize:11, padding:'2px 7px', border:'1px solid #e3e5e8', borderRadius:6, background:'none', cursor:'pointer', color:'#80868b' },
  pageDotActive:{ background:'#fdeef3', color:'#c03060', borderColor:'#f4b8cc', fontWeight:500 },
  addRow:{ display:'flex', gap:6, padding:'10px', borderTop:'1px solid #e3e5e8', flexShrink:0 },
  addInput:{ flex:1, padding:'7px 10px', border:'1px solid #e3e5e8', borderRadius:8, fontSize:12, background:'#f8f9fa' },
  addBtn:{ padding:'7px 12px', background:'#e8547a', color:'#fff', border:'none', borderRadius:8, fontSize:12, fontWeight:500, cursor:'pointer' },
  post:{ background:'#f8f9fa', borderRadius:10, padding:'9px 11px', marginBottom:6 },
  postHead:{ display:'flex', alignItems:'center', gap:6, marginBottom:5 },
  postAv:{ width:22, height:22, borderRadius:'50%', background:'#fdeef3', color:'#c03060', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:600, flexShrink:0 },
  postAuthor:{ fontSize:11, fontWeight:500, color:'#202124', flex:1 },
  postTime:{ fontSize:10, color:'#9aa0a6' },
  iconBtn:{ fontSize:14, border:'none', background:'none', cursor:'pointer', padding:'0 3px', color:'#bdc1c6', lineHeight:1 },
  postBody:{ fontSize:12, color:'#333', lineHeight:1.5 },
  pinBadge:{ fontSize:9, background:'#faeeda', color:'#854f0b', padding:'1px 5px', borderRadius:3, marginLeft:5, fontWeight:500 },
}
